import type { PropsWithChildren } from 'react';
import { Link } from 'react-router';
import { useTypedSearchParams } from 'react-router-typesafe-routes';
import { AvatarWithStatus } from '@/components/common/avatar-with-status';
import useConversationStore from '@/hooks/conversations/use-conversation-store';
import useUserDisplayInfo from '@/hooks/users/use-user-display-info';
import type { ConversationMessageTypeUnion } from '@/models/conversations/conversation';
import ROUTES from '@/routes';
import cn from '@/utils/merge-class-names';
import { isGroupMessage } from '@/utils/message';
import MessageDecoratorFooter from './message-decorator-footer';
import MessageDecoratorRole from './message-decorator-role';
import useMessageInfo from './use-message-info';

interface Props {
  message: ConversationMessageTypeUnion;
  showHeader?: boolean;
}

export default function MessageDecorator({ message, showHeader = true, children }: PropsWithChildren<Props>) {
  const isGroup = isGroupMessage(message);
  const { isMine, isNotification, isStale, isGroupAnnouncement } = useMessageInfo(message);
  const {
    name: displayName,
    avatarUrl,
    role,
  } = useUserDisplayInfo(message.UserID, {
    groupId: isGroup ? message.GroupID : undefined,
    defaultName: message.UserNickName,
    defaultAvatar: message.UserHeadImg,
  });
  const { layout } = useConversationStore();
  const shouldShowHeader = Boolean(!isMine && isGroup && (!isNotification || isGroupAnnouncement) && showHeader);
  const shouldShowAvatar = Boolean(
    !isMine && (!isNotification || isGroupAnnouncement) && message.UserHeadImg && showHeader && layout !== 'mini',
  );

  return (
    <div
      className={cn('flex flex-1 items-start gap-2', isMine ? 'flex-row-reverse pl-5' : 'pr-5', {
        'opacity-50': message.Status === null && !isStale,
      })}>
      {shouldShowAvatar && (
        <Link
          to={ROUTES.CONTACT.USERS.BY_ID.$buildPath({
            params: { userId: message.UserID },
            searchParams: {
              groupId: isGroup ? message.GroupID : undefined,
              source: 'avatar',
            },
          })}>
          <AvatarWithStatus
            imageUrl={avatarUrl}
            label={displayName}
            className='size-8'
            size={[32, 32]}
            defaultAvatar={isGroup ? 'group' : 'user'}
          />
        </Link>
      )}

      <div className='flex flex-col gap-1 w-fit desktop:max-w-[60%]'>
        {shouldShowHeader && (
          <div className='chat-header flex gap-1 items-center max-w-80vw min-w-1'>
            <span className='text-body2 desktop:text-sm truncate text-body'>{displayName}</span>

            {isGroup && <MessageDecoratorRole role={role} />}
          </div>
        )}

        <div className={cn('flex items-end gap-1', { 'flex-row-reverse': isMine })}>
          {children}

          {(!isNotification || isGroupAnnouncement) && <MessageDecoratorFooter message={message} />}
        </div>
      </div>
    </div>
  );
}
